import { transferHandlers, wrap, type Remote } from "comlink";
import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import type {
  LazyHttpDatabase,
  SplitFileConfig,
  SqliteComlinkMod,
} from "sql.js-httpvfs/dist/sqlite.worker";
import sqliteWasmUrl from "../assets/sqlite/generated/sql-wasm.wasm?url";
import sqliteWorkerUrl from "../assets/sqlite/generated/sqlite.worker.js?url";
import { isVolumeFilePath } from "../lib/apiPaths";
import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { Icon } from "./Icon";
import { PressButton } from "./PressButton";
import { PillBadge } from "./Settings/shared";
import { SpreadsheetTable, Td, Th, Thead } from "./SpreadsheetTable";
import { META_SEP } from "./MetaSeparator";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { WavyLine } from "./WavyLine";

const SQLITE_MAX_BYTES_TO_READ = 128 * 1024 * 1024;
const SQLITE_TABLE_LIMIT = 100;
const SQLITE_PAGE_SIZE = 50;
const SQLITE_REQUEST_CHUNK_SIZE = 4096;
const SQLITE_ASSET_VERSION = "range-v2";
const SQLITE_SEARCH_WIDTH = 320;
const SQLITE_COLUMN_MENU_MIN_WIDTH = 160;

type SqlValue = number | string | Uint8Array | null;
type SqlExecResult = { columns: string[]; values: SqlValue[][] };
type SqlColumn = {
  name: string;
  type: string;
  primaryKey: boolean;
  notNull: boolean;
};
type SqlTable = {
  name: string;
  type: string;
  columns: SqlColumn[];
};
type SqlSchema = {
  totalTables: number;
  tables: SqlTable[];
};
type SqlRow = {
  key: string;
  values: Record<string, SqlValue>;
};
type SqlResult = {
  columns: string[];
  rows: SqlRow[];
  hasNext: boolean;
};
type SqliteWorker = { db: Remote<LazyHttpDatabase>; terminate: () => void };
type DatabaseState =
  | { status: "idle" | "loading" | "too-expensive" | "missing-url" }
  | { status: "error"; message: string }
  | { status: "ready"; schema: SqlSchema };
type ResultState =
  | { status: "idle" | "loading" | "too-expensive" }
  | { status: "error"; message: string }
  | { status: "ready"; result: SqlResult };

const Root = styled("div")`
  display: grid;
  grid-template-columns: 176px minmax(0, 1fr);
  gap: 12px;
  width: 100%;
  height: 100%;
  min-height: 0;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(0, 1fr);
  }
`;

const Sidebar = styled("aside")`
  min-width: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;

  @media (max-width: 720px) {
    max-height: 160px;
  }
`;

const TableList = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TableButton = styled(PressButton)`
  border: 0;
  border-radius: 8px !important;
  background: transparent;
  color: var(--text-primary);
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 8px;
  text-align: left;
  cursor: pointer;

  &[data-active="true"] {
    background: var(--alpha4);
  }

  &:hover,
  &:focus-visible {
    background: var(--alpha4);
  }

  &:focus {
    outline: none;
  }
`;

const Main = styled("section")`
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--surface-primary);
  border-radius: 16px;
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  overflow: hidden;
`;

const Toolbar = styled("div")`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding: 0 12px 0 24px;
  border-bottom: 0.5px solid var(--border-alpha);

  @media (max-width: 720px) {
    flex-wrap: wrap;
    padding: 8px 16px;
  }
`;

const Title = styled("div")`
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
`;

const HeaderLabel = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const InfoIcon = styled("div")`
  opacity: 0.2;
  display: flex;
  flex-shrink: 0;
  &:hover {
    opacity: 1;
  }
`;

const SearchRoot = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  width: ${SQLITE_SEARCH_WIDTH}px;
  height: 32px;
  padding: 0 10px;
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 8px;
  transition: box-shadow 100ms ease-out;
  cursor: text;

  &[data-clickable="true"] {
    cursor: pointer;
  }

  &:focus-within {
    outline: 0.5px solid var(--control-bg);
    box-shadow: 0 0 0 3px var(--border-alpha);
  }
`;

const SearchPlaceholder = styled("span")`
  flex: 1;
  min-width: 0;
  font-family: InterVariable;
  font-size: 12px;
  line-height: 16px;
  color: var(--text-primary);
  opacity: 0.3;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const SearchInput = styled("input")`
  all: unset;
  flex: 1;
  min-width: 0;
  font-family: InterVariable;
  font-size: 12px;
  line-height: 16px;
  color: var(--text-primary);

  &::placeholder {
    opacity: 0.3;
  }

  &:focus::placeholder {
    opacity: 0.6;
  }
`;

const SearchClear = styled(PressButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: none;
  color: var(--text-primary);
  opacity: 0.3;
  cursor: pointer;

  &:hover {
    opacity: 1;
  }
`;

const ResultViewport = styled("div")`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const HeaderCell = styled(Th)`
  max-width: 320px;
`;

const Cell = styled(Td)`
  max-width: 320px;
`;

const Footer = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 40px;
  padding: 8px 12px;
  border-top: 0.5px solid var(--border-alpha);
`;

const Pager = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Center = styled("div")`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  color: var(--text-secondary);
`;

function assetHref(assetUrl: string) {
  const url = new URL(assetUrl, location.origin);
  url.searchParams.set("v", SQLITE_ASSET_VERSION);
  return url.href;
}

const workerHref = assetHref(sqliteWorkerUrl);
const wasmHref = assetHref(sqliteWasmUrl);

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function quoteSqlIdentifier(identifier: string) {
  return `"${identifier.replaceAll('"', '""')}"`;
}

async function execRows(
  db: Remote<LazyHttpDatabase>,
  sql: string,
  params: SqlValue[] = []
): Promise<Record<string, SqlValue>[]> {
  const [result] = (await db.exec(sql, params)) as SqlExecResult[];
  if (!result) return [];
  return result.values.map((values) =>
    Object.fromEntries(
      result.columns.map((column, i) => [column, values[i] ?? null])
    )
  );
}

async function sqlNumber(
  db: Remote<LazyHttpDatabase>,
  sql: string,
  params?: SqlValue[]
) {
  const [row] = await execRows(db, sql, params);
  const value = row?.[Object.keys(row)[0] ?? ""];
  return typeof value === "number" ? value : Number(value ?? 0);
}

async function readSchema(db: Remote<LazyHttpDatabase>): Promise<SqlSchema> {
  const totalTables = await sqlNumber(
    db,
    "SELECT COUNT(*) FROM sqlite_schema WHERE type IN ('table', 'view') AND name NOT LIKE 'sqlite_%'"
  );
  const tableRows = await execRows(
    db,
    "SELECT type, name FROM sqlite_schema WHERE type IN ('table', 'view') AND name NOT LIKE 'sqlite_%' ORDER BY name LIMIT ?",
    [SQLITE_TABLE_LIMIT]
  );
  const tables = await Promise.all(
    tableRows.map(async (row) => {
      const name = String(row.name);
      const columns = await execRows(
        db,
        `PRAGMA table_xinfo(${quoteSqlIdentifier(name)})`
      );
      return {
        name,
        type: String(row.type),
        columns: columns.map((column) => ({
          name: String(column.name),
          type: String(column.type ?? ""),
          primaryKey: Number(column.pk ?? 0) > 0,
          notNull: Number(column.notnull ?? 0) === 1,
        })),
      };
    })
  );

  return { totalTables, tables };
}

function escapeLike(value: string) {
  return value
    .replaceAll("\\", "\\\\")
    .replaceAll("%", "\\%")
    .replaceAll("_", "\\_");
}

function formatSqlValue(value: SqlValue) {
  if (value === null) return "NULL";
  if (value instanceof Uint8Array) return `BLOB ${formatBytes(value.length)}`;
  return String(value);
}

function rowKey(row: Record<string, SqlValue>, offset: number) {
  return `${offset}:${Object.entries(row)
    .map(([column, value]) => `${column}=${formatSqlValue(value)}`)
    .join("|")}`;
}

async function queryTable(
  db: Remote<LazyHttpDatabase>,
  table: SqlTable,
  searchColumn: string | null,
  search: string,
  page: number
): Promise<SqlResult> {
  const quotedTable = quoteSqlIdentifier(table.name);
  const trimmedSearch = search.trim();
  const where =
    searchColumn && trimmedSearch
      ? ` WHERE CAST(${quoteSqlIdentifier(searchColumn)} AS TEXT) LIKE ? ESCAPE '\\'`
      : "";
  const searchParams = where ? [`%${escapeLike(trimmedSearch)}%`] : [];
  const offset = page * SQLITE_PAGE_SIZE;
  const rows = await execRows(
    db,
    `SELECT * FROM ${quotedTable}${where} LIMIT ? OFFSET ?`,
    [...searchParams, SQLITE_PAGE_SIZE + 1, offset]
  );
  return {
    columns: table.columns.map((column) => column.name),
    rows: rows.slice(0, SQLITE_PAGE_SIZE).map((row, i) => ({
      key: rowKey(row, offset + i),
      values: row,
    })),
    hasNext: rows.length > SQLITE_PAGE_SIZE,
  };
}

function openUrl(url: string) {
  return new URL(url, location.href).href;
}

function sqlitePreviewUrl(url: string) {
  const previewUrl = new URL(url, location.href);
  if (isVolumeFilePath(previewUrl.pathname)) {
    previewUrl.searchParams.set("sqlite", "1");
  }
  return previewUrl.href;
}

function sqliteErrorMessage(err: unknown) {
  if (err instanceof Error) return err.message;
  return String(err);
}

function isTooExpensiveSqliteError(err: unknown) {
  return sqliteErrorMessage(err).includes("disk I/O error");
}

async function checkSqliteUrl(url: string) {
  const res = await fetch(openUrl(url), {
    method: "HEAD",
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`File check failed with HTTP ${res.status}.`);
  }
  if (res.headers.get("Accept-Ranges") !== "bytes") {
    throw new Error("File server does not support byte ranges.");
  }
}

function sqliteConfig(url: string): SplitFileConfig[] {
  return [
    {
      from: "inline",
      config: {
        serverMode: "full",
        requestChunkSize: SQLITE_REQUEST_CHUNK_SIZE,
        url,
      },
    },
  ];
}

// We talk to the sql.js-httpvfs worker bundle directly instead of through its
// createDbWorker, which hides the Worker handle and so can never be torn down.
// The worker sends the database back under this transfer handler name, and our
// comlink is pinned to the bundle's wire protocol (see assets/sqlite/protocol.test.ts).
transferHandlers.set("WORKERSQLPROXIES", {
  canHandle: (_value): _value is unknown => false,
  serialize() {
    throw new Error("not used");
  },
  deserialize: (port) => {
    const dbPort = port as MessagePort;
    dbPort.start();
    return wrap(dbPort);
  },
});

async function createSqliteWorker(url: string): Promise<SqliteWorker> {
  const worker = new Worker(workerHref);
  try {
    const sqlite = wrap<SqliteComlinkMod>(worker);
    const db = (await sqlite.SplitFileHttpDatabase(
      wasmHref,
      sqliteConfig(url),
      undefined,
      SQLITE_MAX_BYTES_TO_READ
    )) as unknown as Remote<LazyHttpDatabase>;
    return { db, terminate: () => worker.terminate() };
  } catch (err) {
    worker.terminate();
    throw err;
  }
}

function useSqliteDatabase(enabled: boolean, url: string | null) {
  const workerRef = useRef<SqliteWorker | null>(null);
  const [state, setState] = useState<DatabaseState>({ status: "idle" });

  useEffect(() => {
    if (!enabled) return;
    if (!url) {
      setState({ status: "missing-url" });
      return;
    }

    let cancelled = false;
    const previewUrl = sqlitePreviewUrl(url);
    setState({ status: "loading" });
    checkSqliteUrl(previewUrl)
      .then(() => createSqliteWorker(previewUrl))
      .then(async (worker) => {
        if (cancelled) {
          worker.terminate();
          return;
        }

        workerRef.current = worker;
        try {
          const schema = await readSchema(worker.db);
          if (cancelled) {
            worker.terminate();
            return;
          }

          setState({ status: "ready", schema });
        } catch (err) {
          if (cancelled) return;
          worker.terminate();
          workerRef.current = null;
          setState({
            ...(isTooExpensiveSqliteError(err)
              ? { status: "too-expensive" as const }
              : {
                  status: "error" as const,
                  message: sqliteErrorMessage(err),
                }),
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          ...(isTooExpensiveSqliteError(err)
            ? { status: "too-expensive" as const }
            : {
                status: "error" as const,
                message: sqliteErrorMessage(err),
              }),
        });
      });

    return () => {
      cancelled = true;
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, [enabled, url]);

  return { state, workerRef };
}

function useTableResult(
  workerRef: React.RefObject<SqliteWorker | null>,
  table: SqlTable | null,
  searchColumn: string | null,
  search: string,
  page: number
) {
  const [state, setState] = useState<ResultState>({ status: "idle" });

  useEffect(() => {
    const worker = workerRef.current;
    if (!worker || !table) {
      setState({ status: "idle" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });
    queryTable(worker.db, table, searchColumn, search, page)
      .then((result) => {
        if (!cancelled) setState({ status: "ready", result });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          ...(isTooExpensiveSqliteError(err)
            ? { status: "too-expensive" as const }
            : {
                status: "error" as const,
                message: sqliteErrorMessage(err),
              }),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [workerRef, table, searchColumn, search, page]);

  return state;
}

function Loading() {
  return (
    <Center>
      <WavyLine size={24} opacity={0.4} />
    </Center>
  );
}

function Status({ children }: { children: React.ReactNode }) {
  return (
    <Center>
      <Text variant="text-small" color="var(--text-secondary)">
        {children}
      </Text>
    </Center>
  );
}

function columnTypeInfo(column: SqlColumn) {
  return [
    column.type || "No type",
    column.primaryKey ? "primary key" : "",
    column.notNull ? "not null" : "",
  ]
    .filter(Boolean)
    .join(META_SEP);
}

function SqliteSearch({
  columns,
  column,
  onColumnChange,
  search,
  onSearchChange,
}: {
  columns: SqlColumn[];
  column: string | null;
  onColumnChange: (name: string | null) => void;
  search: string;
  onSearchChange: (value: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!column) return;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [column]);

  const sections =
    columns.length === 0
      ? [
          {
            items: [
              {
                type: "button" as const,
                label: "No columns",
                left: undefined,
                disabled: true,
                onClick: () => {},
              },
            ],
          },
        ]
      : [
          {
            items: columns.map((c) => ({
              type: "button" as const,
              key: c.name,
              left: undefined,
              label: c.name,
              selected: c.name === column,
              right:
                c.name === column ? (
                  <Icon name="check" size={12} color="var(--text-secondary)" />
                ) : undefined,
              onClick: () => onColumnChange(c.name),
            })),
          },
        ];

  if (!column) {
    return (
      <Dropdown
        align="bottom-left"
        minWidth={SQLITE_COLUMN_MENU_MIN_WIDTH}
        maxWidth={SQLITE_SEARCH_WIDTH}
        opener={
          <SearchRoot data-clickable="true">
            <PillBadge icon="search" interactive>
              Select column
            </PillBadge>
            <SearchPlaceholder>Search…</SearchPlaceholder>
          </SearchRoot>
        }
        sections={sections}
      />
    );
  }

  return (
    <SearchRoot>
      <Dropdown
        align="bottom-left"
        minWidth={SQLITE_COLUMN_MENU_MIN_WIDTH}
        maxWidth={SQLITE_SEARCH_WIDTH}
        opener={
          <PillBadge icon="search" interactive>
            {column}
          </PillBadge>
        }
        sections={sections}
      />
      <SearchInput
        ref={inputRef}
        placeholder="Search…"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Backspace" && search === "") onColumnChange(null);
        }}
      />
      {search && (
        <SearchClear onPress={() => onSearchChange("")}>
          <Icon name="x" size={12} />
        </SearchClear>
      )}
    </SearchRoot>
  );
}

export function SqlitePreview({
  enabled,
  url,
}: {
  enabled: boolean;
  url: string | null;
}) {
  const { state, workerRef } = useSqliteDatabase(enabled, url);
  const [activeTableName, setActiveTableName] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchColumn, setSearchColumn] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  const schema = state.status === "ready" ? state.schema : null;
  const tables = schema?.tables ?? [];
  const activeTable =
    tables.find((table) => table.name === activeTableName) ?? tables[0] ?? null;
  const resultState = useTableResult(
    workerRef,
    activeTable,
    searchColumn,
    search,
    page
  );

  useEffect(() => {
    if (activeTable && activeTable.name !== activeTableName) {
      setActiveTableName(activeTable.name);
      setSearch("");
      setSearchColumn(null);
      setPage(0);
    }
  }, [activeTable, activeTableName]);

  if (state.status === "idle" || state.status === "loading") return <Loading />;
  if (state.status === "missing-url") {
    return <Status>SQLite range preview needs a file URL.</Status>;
  }
  if (state.status === "too-expensive") {
    return (
      <Status>
        This query read more than {formatBytes(SQLITE_MAX_BYTES_TO_READ)}.
      </Status>
    );
  }
  if (state.status === "error") {
    return <Status>Unable to open SQLite database: {state.message}</Status>;
  }
  if (!schema) return <Loading />;
  if (!activeTable) return <Status>No tables found.</Status>;

  const result = resultState.status === "ready" ? resultState.result : null;
  const columnsByName = new Map(
    activeTable.columns.map((column) => [column.name, column] as const)
  );
  const hasPrevious = page > 0;
  const hasNext = result?.hasNext ?? false;

  return (
    <Root>
      <Sidebar>
        <TableList>
          {tables.map((table) => (
            <TableButton
              key={table.name}
              data-active={table.name === activeTable.name}
              onPress={() => {
                setActiveTableName(table.name);
                setSearch("");
                setSearchColumn(null);
                setPage(0);
              }}
            >
              <Icon
                name={table.type === "view" ? "layers" : "table"}
                size={16}
              />
              <Text ellipsis style={{ flex: 1, minWidth: 0 }}>
                {table.name}
              </Text>
            </TableButton>
          ))}
        </TableList>
      </Sidebar>
      <Main>
        <Toolbar>
          <Title>
            <Text variant="text-regular-md" ellipsis>
              {activeTable.name}
            </Text>
          </Title>
          <SqliteSearch
            columns={activeTable.columns}
            column={searchColumn}
            onColumnChange={(name) => {
              setSearchColumn(name);
              setPage(0);
            }}
            search={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(0);
            }}
          />
        </Toolbar>
        <ResultViewport>
          {resultState.status === "loading" && <Loading />}
          {resultState.status === "error" && (
            <Status>Unable to query table.</Status>
          )}
          {resultState.status === "too-expensive" && (
            <Status>
              This query read more than {formatBytes(SQLITE_MAX_BYTES_TO_READ)}.
            </Status>
          )}
          {result && result.rows.length === 0 && (
            <Status>No rows found.</Status>
          )}
          {result && result.rows.length > 0 && (
            <SpreadsheetTable>
              <Thead>
                <tr>
                  {result.columns.map((column) => {
                    const info = columnsByName.get(column);
                    return (
                      <HeaderCell key={column}>
                        <HeaderLabel>
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                            ellipsis
                          >
                            {column}
                          </Text>
                          {info && (
                            <Tooltip content={columnTypeInfo(info)}>
                              <InfoIcon>
                                <Icon name="info" size={12} />
                              </InfoIcon>
                            </Tooltip>
                          )}
                        </HeaderLabel>
                      </HeaderCell>
                    );
                  })}
                </tr>
              </Thead>
              <tbody>
                {result.rows.map((row) => (
                  <tr key={row.key}>
                    {result.columns.map((column) => (
                      <Cell key={column}>
                        <Text variant="text-small">
                          {formatSqlValue(row.values[column] ?? null)}
                        </Text>
                      </Cell>
                    ))}
                  </tr>
                ))}
              </tbody>
            </SpreadsheetTable>
          )}
        </ResultViewport>
        <Footer>
          <Text variant="text-small" color="var(--text-secondary)">
            Page {page + 1}
          </Text>
          <Pager>
            <Button
              type="button"
              variant="secondary"
              icon="chevron-left"
              disabled={!hasPrevious}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            />
            <Button
              type="button"
              variant="secondary"
              icon="chevron-right"
              disabled={!hasNext}
              onClick={() => setPage((p) => p + 1)}
            />
          </Pager>
        </Footer>
      </Main>
    </Root>
  );
}
